/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect, useRef, useState } from 'react';

// Store
import useStore from '@store/index';

// I18n
import { useTranslation } from 'react-i18next';

// Utils
import {
	cloneDeep,
	isValidObjectId,
	haveSameTechnicalDocumentsNames,
} from '@utils/functions';

// I18n
import { useUploadMedia } from 'shared/api/upload-media';

// Router
import { useParams } from 'react-router';

// Apis
import { useRemoveMedia } from 'shared/api/remove-media';
import { useUpdateAdditionalityProject } from '../api/update-additionality-project';
import dayjs from 'dayjs';
import { useRemovePrivateMedia } from 'shared/api/remove-private-media';
import { useActivateAdditionalityProject } from '../api/activate-additionality-project';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

export const useAdditionalityProjectUpdate = (props: {
	form: any;
	onBack: () => void;
}) => {
	const viewport = useRef<HTMLDivElement>(null);
	const { onBack } = props;

	/**************************** Store ****************************/
	const {
		idsLogoToDelete,
		setIdsLogoToDelete,
		idsCoverUrlToDelete,
		setIdsCoverUrlToDelete,
		idsPublicTechnicalDocumentsToDelete,
		idsInternalTechnicalDocumentsToDelete,
		setIdsPublicTechnicalDocumentsToDelete,
		setIdsInternalTechnicalDocumentsToDelete,
		setUpdateAdditionalityProjectFormOpened,
		setInitialAdditionalityData,
		initialAdditionalityData,
		additionalityProjectsPage,
		additionalityProjectsPageSize,
	} = useStore();

	/**************************** States ****************************/
	const [producerId, setProducerId] = useState('');
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);

	const [logoValue, setLogoValue] = useState<any>(null);
	const [coverValue, setCoverUrlValue] =
		useState<any>(null);

	const [
		isLoadingPublicTechnicalDocuments,
		setIsLoadingPublicTechnicalDocuments,
	] = useState(false);

	const [
		isLoadingInternalTechnicalDocuments,
		setIsLoadingInternalTechnicalDocuments,
	] = useState(false);

	/**************************** I18n ****************************/
	const { t } = useTranslation();

	/**************************** APIs ****************************/
	const removeMedia = useRemoveMedia();

	const removePrivateMedia = useRemovePrivateMedia();

	const { projectId } = useParams();

	const uploadMediaLogo = useUploadMedia();

	const uploadMediaCoverUrl = useUploadMedia();

	const activateAdditionalityProject =
		useActivateAdditionalityProject();

	// Update request
	const updateAdditionalityProject =
		useUpdateAdditionalityProject(
			additionalityProjectsPage,
			additionalityProjectsPageSize
		);

	/* ******************************** HELPERS *********************************** */

	const handleMediaCoverUrlValue = (
		idMedia: string,
		media: any
	) => {
		if (!idMedia) {
			return null;
		}
		if (idMedia && media && !coverValue) {
			return new File([''], media.filename);
		} else if (idMedia && media && coverValue) {
			return coverValue;
		} else if (idMedia && !media && coverValue) {
			return coverValue;
		}
	};

	const handleMediaLogoValue = (
		idMedia: string,
		media: any
	) => {
		if (!idMedia) {
			return null;
		}
		if (idMedia && media && !logoValue) {
			return new File([''], media.filename);
		} else if (idMedia && media && logoValue) {
			return logoValue;
		} else if (idMedia && !media && logoValue) {
			return logoValue;
		}
	};

	function convertArrayTechnologiesToLabelValue(
		array: any
	) {
		return array.map((item: any) => ({
			label: t(item.label),
			value: item.value,
		}));
	}

	const handleSubmitForm = async (
		form: any,
		projectId: string
	) => {
		form.validate();

		if (form.isValid()) {
			const {
				name,
				unit,
				countryCode,
				place,
				cover,
				logo,
				technology,
				twitter,
				linkedin,
				description,
				technicalDocuments,
				internalTechnicalDocuments,
				active,
				website,
				financing,
				typeOfFinancing,
				dueDate,
				openingDate,
				supportedBy,
			} = form.getValues();

			if (form.isTouched('active')) {
				activateAdditionalityProject.mutate({
					active: active,
					ids: projectId ? [projectId] : [''],
				});
			}

			const twitterParts = twitter
				? twitter.split('/')
				: [];
			const twitterCompanyName =
				twitterParts[twitterParts.length - 1];

			const linkedInCompanyName = linkedin
				? linkedin.split('/company/')[1]
				: '';

			const unlimited = typeOfFinancing == 'UNLIMITED';

			const parsedDueDate =
				dayjs(dueDate).format('YYYY-MM-DD');

			const parsedOpeningDate =
				dayjs(openingDate).format('YYYY-MM-DD');

			const bodyUpdateAdditionalityProject = {
				...(form.isDirty('name')
					? {
							name,
						}
					: {}),
				...(form.isDirty('unit')
					? {
							unit,
						}
					: {}),

				...(form.isDirty('supportedBy')
					? {
							supportedBy,
						}
					: {}),

				...(form.isDirty('dueDate') && unlimited
					? {
							dueDate: new Date(parsedDueDate),
						}
					: {}),
				...(form.isDirty('openingDate')
					? {
							openingDate: new Date(parsedOpeningDate),
						}
					: {}),

				...(!unlimited && form.isDirty('financing')
					? { financing: { target_euro: financing } }
					: {}),

				...(form.isDirty('countryCode') ||
				form.isDirty('place')
					? {
							location: {
								countryCode: countryCode,
								place: place,
							},
						}
					: {}),

				...(form.isDirty('cover')
					? {
							cover,
						}
					: {}),
				...(form.isDirty('logo')
					? {
							logo,
						}
					: {}),

				...(form.isDirty('technology')
					? {
							technology,
						}
					: {}),
				...(form.isDirty('description')
					? {
							description,
						}
					: {}),

				...(form.isDirty('linkedin') ||
				form.isDirty('twitter') ||
				form.isDirty('website')
					? {
							social: {
								...(form.isDirty('linkedin')
									? {
											linkedIn: linkedInCompanyName,
										}
									: {}),
								...(form.isDirty('twitter')
									? {
											twitter: twitterCompanyName,
										}
									: {}),
								...(form.isDirty('website')
									? {
											website: website,
										}
									: {}),
							},
						}
					: {}),

				...(initialAdditionalityData &&
				initialAdditionalityData.publicSpecs &&
				Array.isArray(
					initialAdditionalityData.publicSpecs
				) &&
				!haveSameTechnicalDocumentsNames(
					initialAdditionalityData.publicSpecs,
					form.getValues().technicalDocuments
				) &&
				technicalDocuments &&
				Array.isArray(technicalDocuments)
					? {
							publicSpecs: technicalDocuments.map(
								(item: any) => item.id
							),
						}
					: {}),

				...(initialAdditionalityData &&
				initialAdditionalityData.internalSpecs &&
				Array.isArray(
					initialAdditionalityData.internalSpecs
				) &&
				!haveSameTechnicalDocumentsNames(
					initialAdditionalityData.internalSpecs,
					form.getValues().internalTechnicalDocuments
				) &&
				internalTechnicalDocuments &&
				Array.isArray(internalTechnicalDocuments)
					? {
							internalSpecs:
								internalTechnicalDocuments.map(
									(item: any) => item.id
								),
						}
					: {}),
			};

			let isDirtyForm = false;
			isDirtyForm =
				form.isDirty('name') ||
				form.isDirty('supportedBy') ||
				form.isDirty('unit') ||
				(form.isDirty('dueDate') && unlimited) ||
				form.isDirty('openingDate') ||
				(!unlimited && form.isDirty('financing')) ||
				form.isDirty('countryCode') ||
				form.isDirty('place') ||
				form.isDirty('cover') ||
				form.isDirty('logo') ||
				form.isDirty('technology') ||
				form.isDirty('description') ||
				form.isDirty('linkedin') ||
				form.isDirty('twitter') ||
				form.isDirty('website') ||
				(initialAdditionalityData &&
					initialAdditionalityData.publicSpecs &&
					Array.isArray(
						initialAdditionalityData.publicSpecs
					) &&
					!haveSameTechnicalDocumentsNames(
						initialAdditionalityData.publicSpecs,
						form.getValues().technicalDocuments
					) &&
					technicalDocuments &&
					Array.isArray(technicalDocuments)) ||
				(initialAdditionalityData &&
					initialAdditionalityData.internalSpecs &&
					Array.isArray(
						initialAdditionalityData.internalSpecs
					) &&
					!haveSameTechnicalDocumentsNames(
						initialAdditionalityData.internalSpecs,
						form.getValues().internalTechnicalDocuments
					) &&
					internalTechnicalDocuments &&
					Array.isArray(internalTechnicalDocuments));

			if (isDirtyForm) {
				updateAdditionalityProject.mutate({
					data: bodyUpdateAdditionalityProject,
					id: projectId ? projectId : '',
				});
				onBack();
			} else {
				onBack();
			}
			const cloneIdsLogoToDelete = cloneDeep(
				idsLogoToDelete
			);

			if (
				idsLogoToDelete &&
				Array.isArray(idsLogoToDelete) &&
				idsLogoToDelete.length > 0
			) {
				const uniqueIds = [
					...new Set(cloneIdsLogoToDelete),
				];
				await Promise.all(
					uniqueIds
						.filter((id: any) => id !== logo)
						.map(async (id: any) => {
							if (isValidObjectId(id)) {
								removeMedia.mutate(id);
							}
						})
				);
			}

			const cloneIdsCoverUrlToDelete = cloneDeep(
				idsCoverUrlToDelete
			);

			if (
				idsCoverUrlToDelete &&
				Array.isArray(idsCoverUrlToDelete) &&
				idsCoverUrlToDelete.length > 0
			) {
				const uniqueIds = [
					...new Set(cloneIdsCoverUrlToDelete),
				];

				await Promise.all(
					uniqueIds
						.filter((id: any) => id !== cover)
						.map(async (id: any) => {
							if (isValidObjectId(id)) {
								removeMedia.mutate(id);
							}
						})
				);
			}

			const cloneIdsPublicTechnicalDocumentsToDelete =
				cloneDeep(idsPublicTechnicalDocumentsToDelete);

			const parsedPublicTechnicalDocuments =
				technicalDocuments &&
				Array.isArray(technicalDocuments) &&
				technicalDocuments.length > 0
					? technicalDocuments.map((item: any) => item.id)
					: [];

			if (
				idsPublicTechnicalDocumentsToDelete &&
				Array.isArray(
					idsPublicTechnicalDocumentsToDelete
				) &&
				idsPublicTechnicalDocumentsToDelete.length > 0
			) {
				const uniqueIds = [
					...new Set([
						...cloneIdsPublicTechnicalDocumentsToDelete,
					]),
				];
				await Promise.all(
					uniqueIds
						.filter(
							(id: string) =>
								!parsedPublicTechnicalDocuments.includes(
									id
								)
						)
						.map(async (id: string) => {
							if (isValidObjectId(id)) {
								removeMedia.mutate(id);
							}
						})
				);
			}

			setIdsCoverUrlToDelete([]);
			setIdsLogoToDelete([]);
			setIdsPublicTechnicalDocumentsToDelete([]);
		}
	};

	const handleFileLogo = (
		file: File | null,
		form: any
	) => {
		if (file) {
			const formData = new FormData();
			formData.append('entity', form.getValues().entity);
			formData.append('public', 'true');
			formData.append('file', file);
			uploadMediaLogo.mutate(formData);

			setLogoValue(file);
		} else {
			const idLogo = form.getValues().logo;

			const newIdsLogoToDelete = [
				...idsLogoToDelete,
				idLogo,
			];

			setIdsLogoToDelete(newIdsLogoToDelete);

			form.setValues({
				logo: null,
			});
		}
	};

	const handleFileCoverUrl = (
		file: File | null,
		form: any
	) => {
		if (file) {
			const formData = new FormData();
			formData.append('entity', form.getValues().entity);
			formData.append('public', 'true');
			formData.append('file', file);
			uploadMediaCoverUrl.mutate(formData);
			setCoverUrlValue(file);
		} else {
			const idCoverUrl = form.getValues().cover;
			const newIdsCoverUrlToDelete = [
				...idsCoverUrlToDelete,
				idCoverUrl,
			];
			setIdsCoverUrlToDelete(newIdsCoverUrlToDelete);
			form.setValues({
				cover: null,
			});
		}
	};

	const handleReloadFiles = (
		filteredFiles: any,
		form: any,
		dataApi: any
	) => {
		const allDocuments =
			form.getValues().technicalDocuments;

		const parsedAllDocuments = allDocuments.map(
			(obj: any) => {
				const match = dataApi.publicSpecs.find(
					(item: any) => item.id === obj.id
				);

				return {
					...obj,
					...(match && match.id ? { id: match.id } : {}),
				};
			}
		);

		const missingInFiltered = parsedAllDocuments.filter(
			(resFile: any) =>
				!filteredFiles.some(
					(filtFile: any) => filtFile.id === resFile.id
				)
		);

		const missingItem =
			missingInFiltered &&
			Array.isArray(missingInFiltered) &&
			missingInFiltered.length > 0
				? missingInFiltered[0].id
				: {};

		const newIdsPublicTechnicalDocumentsToDelete = [
			...idsPublicTechnicalDocumentsToDelete,
			missingItem,
		];

		setIdsPublicTechnicalDocumentsToDelete(
			newIdsPublicTechnicalDocumentsToDelete
		);

		form
			.getInputProps('technicalDocuments')
			.onChange(filteredFiles);
	};

	const handleUploadFiles = (files: any, form: any) => {
		form
			.getInputProps('technicalDocuments')
			.onChange(files);

		const ids = files.map(
			(item: { filename: string; id: string }) => item.id
		);

		const newIdsPublicTechnicalDocumentsToDelete = [
			...idsPublicTechnicalDocumentsToDelete,
			...ids,
		];
		setIdsPublicTechnicalDocumentsToDelete(
			newIdsPublicTechnicalDocumentsToDelete
		);
	};

	const handleUploadInternalFiles = (
		files: any,
		form: any
	) => {
		form
			.getInputProps('internalTechnicalDocuments')
			.onChange(files);

		const ids = files.map(
			(item: { filename: string; id: string }) => item.id
		);

		const newIdsInternalTechnicalDocumentsToDelete = [
			...idsInternalTechnicalDocumentsToDelete,
			...ids,
		];
		setIdsInternalTechnicalDocumentsToDelete(
			newIdsInternalTechnicalDocumentsToDelete
		);
	};

	const handleReloadInternalFiles = (
		filteredFiles: any,
		form: any,
		dataApi: any
	) => {
		const allDocuments =
			form.getValues().internalTechnicalDocuments;

		const parsedAllDocuments = allDocuments.map(
			(obj: any) => {
				const match = dataApi.internalSpecs.find(
					(item: any) => item.id === obj.id
				);

				return {
					...obj,
					...(match && match.id ? { id: match.id } : {}),
				};
			}
		);

		const missingInFiltered = parsedAllDocuments.filter(
			(resFile: any) =>
				!filteredFiles.some(
					(filtFile: any) => filtFile.id === resFile.id
				)
		);

		const missingItem =
			missingInFiltered &&
			Array.isArray(missingInFiltered) &&
			missingInFiltered.length > 0
				? missingInFiltered[0].id
				: {};

		const newIdsInternalTechnicalDocumentsToDelete = [
			...idsPublicTechnicalDocumentsToDelete,
			missingItem,
		];

		setIdsInternalTechnicalDocumentsToDelete(
			newIdsInternalTechnicalDocumentsToDelete
		);

		form
			.getInputProps('internalTechnicalDocuments')
			.onChange(filteredFiles);
	};

	const handleChangeEntity = (
		value: string,
		form: any
	) => {
		form.setFieldValue('entity', value);
		form.setFieldValue('project', null);
		setProducerId(value);
	};

	const handleCloseDrawerAddAdditionalityProject = async (
		form: any,
		data: any
	) => {
		if (
			idsLogoToDelete &&
			Array.isArray(idsLogoToDelete) &&
			idsLogoToDelete.length > 0
		) {
			const uniqueIds = [...new Set(idsLogoToDelete)];
			await Promise.all(
				uniqueIds
					.filter((id: string) => id !== data.logo)
					.map(async (id) => {
						if (isValidObjectId(id)) {
							removeMedia.mutate(id);
						}
					})
			);
		}

		if (
			idsCoverUrlToDelete &&
			Array.isArray(idsCoverUrlToDelete) &&
			idsCoverUrlToDelete.length > 0
		) {
			await Promise.all(
				idsCoverUrlToDelete
					.filter((id: string) => id !== data.cover)
					.map(async (id) => {
						if (isValidObjectId(id)) {
							removeMedia.mutate(id);
						}
					})
			);
		}

		const oldPublicTechnicalDocuments =
			data.publicSpecs && Array.isArray(data.publicSpecs)
				? data.publicSpecs.map((item: any) => item.id)
				: [];

		if (
			idsPublicTechnicalDocumentsToDelete &&
			Array.isArray(
				idsPublicTechnicalDocumentsToDelete
			) &&
			idsPublicTechnicalDocumentsToDelete.length > 0
		) {
			const uniqueIds = [
				...new Set(idsPublicTechnicalDocumentsToDelete),
			];

			await Promise.all(
				uniqueIds
					.filter(
						(item) =>
							!oldPublicTechnicalDocuments.includes(item)
					)
					.map(async (id) => {
						if (isValidObjectId(id)) {
							removeMedia.mutate(id);
						}
					})
			);
		}

		const oldInternalTechnicalDocuments =
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			data.internalSpecs.map((item: any) => item.id);

		if (
			idsInternalTechnicalDocumentsToDelete &&
			Array.isArray(
				idsInternalTechnicalDocumentsToDelete
			) &&
			idsInternalTechnicalDocumentsToDelete.length > 0
		) {
			const uniqueIds = [
				...new Set(idsInternalTechnicalDocumentsToDelete),
			];

			await Promise.all(
				uniqueIds
					.filter(
						(item) =>
							!oldInternalTechnicalDocuments.includes(
								item
							)
					)
					.map(async (id) => {
						if (isValidObjectId(id)) {
							removePrivateMedia.mutate(id);
						}
					})
			);
		}

		setIdsCoverUrlToDelete([]);
		setIdsLogoToDelete([]);
		setIdsPublicTechnicalDocumentsToDelete([]);
		setIdsInternalTechnicalDocumentsToDelete([]);
		setUpdateAdditionalityProjectFormOpened(false);
		setIsOpenModalCancel(false);
		form.reset();
	};
	/********************************************************************************************/
	useEffect(() => {
		if (uploadMediaLogo.isSuccess) {
			if (
				uploadMediaLogo.data &&
				uploadMediaLogo.data.id
			) {
				props.form.setValues({
					logo: uploadMediaLogo.data.id,
				});

				const idLogo = uploadMediaLogo.data.id;

				const newIdsLogoToDelete = [
					...idsLogoToDelete,
					idLogo,
				];

				setIdsLogoToDelete(newIdsLogoToDelete);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadMediaLogo.isSuccess]);

	useEffect(() => {
		if (uploadMediaCoverUrl.isSuccess) {
			if (
				uploadMediaCoverUrl.data &&
				uploadMediaCoverUrl.data.id
			) {
				props.form.setValues({
					cover: uploadMediaCoverUrl.data.id,
				});

				const idCoverUrl = uploadMediaCoverUrl.data.id;
				const newIdsCoverUrlToDelete = [
					...idsCoverUrlToDelete,
					idCoverUrl,
				];
				setIdsCoverUrlToDelete(newIdsCoverUrlToDelete);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadMediaCoverUrl.isSuccess]);
	/********************************************************************************************/
	return {
		handleChangeEntity,
		idsLogoToDelete,
		setIdsLogoToDelete,
		idsCoverUrlToDelete,
		setIdsCoverUrlToDelete,
		idsPublicTechnicalDocumentsToDelete,
		setIdsPublicTechnicalDocumentsToDelete,
		producerId,
		setProducerId,
		logoValue,
		setLogoValue,
		coverValue,
		setCoverUrlValue,
		isLoadingPublicTechnicalDocuments,
		setIsLoadingPublicTechnicalDocuments,
		removeMedia,
		uploadMediaLogo,
		uploadMediaCoverUrl,
		handleMediaCoverUrlValue,
		handleMediaLogoValue,
		convertArrayTechnologiesToLabelValue,
		handleSubmitForm,
		updateAdditionalityProject,
		handleFileLogo,
		handleFileCoverUrl,
		handleReloadFiles,
		handleUploadFiles,
		viewport,
		isOpenModalCancel,
		setIsOpenModalCancel,
		handleCloseDrawerAddAdditionalityProject,
		handleUploadInternalFiles,
		projectId,
		handleReloadInternalFiles,
		setIsLoadingInternalTechnicalDocuments,
		isLoadingInternalTechnicalDocuments,
		setInitialAdditionalityData,
		initialAdditionalityData,
	};
};
